// 업체 선택 모달 (ADMIN.md S3) — 셸이 렌더한다. 데이터 GET /auth/my-companies (scope.js 캐시 공유).
// 선택 → POST /auth/switch-company → 토큰·세션 갱신 → company-switched 이벤트 → 셸이 화면을 remount.
// 페이지 리로드 없음. 닫기 3경로: ✕ · ESC · 바깥 클릭.
// props: open, user, onClose, onSwitched(user), onError(msg), isMobile
import React, { useState, useEffect } from 'react';
import { T, S, R, FONT, NUM } from '../theme';
import { API, authFetch } from '../../utils/api';
import { useCompanyScope } from '../scope';

var wrap = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' };
var box = { width: 'min(520px, 94vw)', maxHeight: '82vh', background: T.card, borderRadius: R.lg, boxShadow: T.shadowMenu, display: 'flex', flexDirection: 'column', overflow: 'clip' };
var headSt = { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px 10px', borderBottom: '1px solid ' + T.bd };
var titleSt = { fontSize: FONT.title, fontWeight: 800, color: T.navy, letterSpacing: '-.3px', flex: 1 };
var xSt = { border: '1px solid ' + T.bd, borderRadius: R.md, background: T.card, fontSize: 15, color: T.muted, minWidth: 32, minHeight: 32, cursor: 'pointer', padding: 0 };
var searchWrap = { padding: '10px 16px' };
var listSt = { overflowY: 'auto', flex: 1, borderTop: '1px solid ' + T.bdSoft };
var rowSt = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', boxSizing: 'border-box', height: 40, padding: '0 16px', border: 'none', borderBottom: '1px solid ' + T.bdSoft, background: T.card, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', fontSize: FONT.body, color: T.body };
var rowOn = Object.assign({}, rowSt, { background: T.accentTint, color: T.navy, fontWeight: 700 });
var allRow = Object.assign({}, rowSt, { fontWeight: 700, background: T.head });
var allRowOn = Object.assign({}, allRow, { background: T.accentTint, color: T.navy });
var nameSt = { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var bizSt = { fontSize: FONT.label, color: T.faint, flexShrink: 0, fontVariantNumeric: NUM.fontVariantNumeric };
var cntSt = { fontSize: FONT.label, color: T.muted, flexShrink: 0, minWidth: 48, textAlign: 'right', fontVariantNumeric: NUM.fontVariantNumeric };
var curSt = { fontSize: FONT.badge, fontWeight: 800, color: T.accent, flexShrink: 0 };
var emptySt = { padding: '18px 16px', fontSize: FONT.body, color: T.faint, textAlign: 'center' };
var footSt = { padding: '8px 16px', borderTop: '1px solid ' + T.bd, fontSize: FONT.label, color: T.faint, background: T.head };
var searchSt = Object.assign({}, S.input, { height: 32 });

function CompanyScopeModal(props) {
  var open = !!props.open, onClose = props.onClose;
  var sc = useCompanyScope(props.user);
  // 배열 구조분해 — eslint react-hooks 가 setter 를 stable 로 인식해야 exhaustive-deps 경고가 안 난다(SlidePanel.js 와 같은 이유).
  var [q, setQ] = useState('');
  var [busy, setBusy] = useState(false);

  useEffect(function () {
    if (!open) { setQ(''); return undefined; }
    var on = function (e) { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', on);
    return function () { window.removeEventListener('keydown', on); };
  }, [open, onClose]);

  if (!open) return null;

  var s = q.trim().toLowerCase();
  var rows = sc.companies.filter(function (c) {
    if (!s) return true;
    return (c.name || '').toLowerCase().indexOf(s) >= 0 || (c.bizNo || '').indexOf(s) >= 0;
  });

  var pick = function (cid) {
    if (busy) return;
    if (cid === sc.companyId) { if (onClose) onClose(); return; }
    setBusy(true);
    authFetch(API + '/auth/switch-company', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ companyId: cid }) })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        setBusy(false);
        if (d && d.success && d.token && d.user) {
          localStorage.setItem('autodoc_token', d.token);
          sessionStorage.setItem('autodoc_user', JSON.stringify(d.user));
          window.dispatchEvent(new CustomEvent('company-switched', { detail: d.user }));
          if (props.onSwitched) props.onSwitched(d.user);
          if (onClose) onClose();
        } else if (props.onError) props.onError((d && d.error) || '업체 전환 실패');
      })
      .catch(function () { setBusy(false); if (props.onError) props.onError('서버 오류'); });
  };

  return (<div style={wrap} data-testid="v2-scope-backdrop" onClick={function () { if (!busy && onClose) onClose(); }}>
    <div role="dialog" aria-label="업체 선택" style={box} onClick={function (e) { e.stopPropagation(); }}>
      <div style={headSt}>
        <span style={titleSt}>업체 선택</span>
        <button type="button" style={xSt} title="닫기" aria-label="닫기" onClick={onClose}>✕</button>
      </div>
      <div style={searchWrap}>
        <input style={searchSt} value={q} autoFocus aria-label="업체 검색" placeholder="업체명 / 사업자번호 검색..."
          onChange={function (e) { setQ(e.target.value); }} />
      </div>
      <div style={listSt}>
        {sc.isAdmin ? <button type="button" style={sc.allMode ? allRowOn : allRow} onClick={function () { pick(0); }}>
          <span style={nameSt}>🌐 전체 업체</span>
          {sc.allMode ? <span style={curSt}>현재</span> : null}
        </button> : null}
        {rows.map(function (c) {
          var on = c.id === sc.companyId;
          return (<button type="button" key={c.id} style={on ? rowOn : rowSt} onClick={function () { pick(c.id); }}>
            <span style={nameSt}>{c.name}</span>
            {c.bizNo ? <span style={bizSt}>{c.bizNo}</span> : null}
            <span style={cntSt}>{(c.carCount || 0) + '대'}</span>
            {on ? <span style={curSt}>현재</span> : null}
          </button>);
        })}
        {rows.length === 0 ? <div style={emptySt}>검색 결과가 없습니다.</div> : null}
      </div>
      <div style={footSt}>{busy ? '전환 중...' : '전체 ' + sc.companies.length + '개 업체'}</div>
    </div>
  </div>);
}

export default CompanyScopeModal;
